import { cx, EmptyState, Spinner } from '@freightapi/ui'
import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'
import { type Mode, signOut } from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { ModeProvider, useMode } from '../lib/mode'
import { SessionProvider, useSession } from '../lib/session'
import { NAV } from '../nav'

/**
 * The signed-in chrome. It is a pathless layout route, so `/`, `/shipments` and every later
 * section share one navigation and one session check without any of them repeating it.
 *
 * `/login`, `/signup` and `/welcome` sit outside this layout on purpose: they are reached without
 * a session, and a navigation bar to pages you cannot open yet is noise.
 */
export const Route = createFileRoute('/_app')({ component: AppLayout })

function AppLayout() {
  const { t } = useConsoleI18n()
  const state = useSession()
  const navigate = useNavigate()

  // A session that is gone is not an error screen: it is a login screen. A session that still owes
  // its second factor (M4-12) is not an error screen either: the API refuses every call this
  // layout would make, so the one useful thing to render is the code prompt.
  const pendingTwoFactor = state.status === 'authenticated' && state.session.two_factor.pending
  useEffect(() => {
    if (state.status === 'anonymous') void navigate({ to: '/login', replace: true })
    else if (pendingTwoFactor) void navigate({ to: '/two-factor', replace: true })
  }, [state.status, pendingTwoFactor, navigate])

  const onSignOut = useCallback(async () => {
    await signOut().catch(() => undefined)
    window.location.assign('/login')
  }, [])

  if (state.status === 'loading' || state.status === 'anonymous' || pendingTwoFactor) {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }

  if (state.status === 'error') {
    return <EmptyState title={t('error.title')} description={t(state.error.messageKey)} />
  }

  return (
    <div className="flex flex-col gap-6 md:flex-row md:gap-10">
      <div className="flex flex-col gap-6 md:w-52 md:shrink-0">
        <Navigation />
        <div className="flex flex-col items-start gap-0.5 border-line px-3 md:border-t md:pt-4">
          <p className="font-medium text-body text-ink">{state.session.organization.name}</p>
          <p className="break-all text-caption text-muted">{state.session.user.email}</p>
          <button
            type="button"
            onClick={() => void onSignOut()}
            className="min-h-touch text-body text-brand-700 underline"
          >
            {t('button.sign_out')}
          </button>
        </div>
      </div>
      <div className="min-w-0 grow">
        <SessionProvider session={state.session}>
          <ModeProvider liveAvailable={state.session.organization.live_enabled}>
            <div className="flex flex-col gap-6">
              <ModeSwitch />
              <Outlet />
            </div>
          </ModeProvider>
        </SessionProvider>
      </div>
    </div>
  )
}

/**
 * The test/live switch, at the top of every signed-in screen (M3-03). Live is disabled — with the
 * reason on the control itself — until Cargofollow enables it, so the switch explains the state of
 * the organisation instead of failing on the next request.
 */
function ModeSwitch() {
  const { t } = useConsoleI18n()
  const { mode, setMode, liveAvailable } = useMode()

  return (
    <fieldset className="flex items-center justify-end gap-2 border-0 p-0">
      <legend className="float-left pr-2 text-caption text-muted">{t('mode.label')}</legend>
      <div className="flex rounded-card border border-line">
        {(['test', 'live'] as const satisfies readonly Mode[]).map((value) => {
          const disabled = value === 'live' && !liveAvailable
          return (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              disabled={disabled}
              title={disabled ? t('mode.live.disabled') : undefined}
              onClick={() => setMode(value)}
              className={cx(
                'min-h-touch px-3 font-medium text-body first:rounded-l-card last:rounded-r-card',
                mode === value ? 'bg-brand-50 text-brand-700' : 'text-muted',
                disabled ? 'opacity-50' : 'hover:bg-surface',
              )}
            >
              {t(`mode.${value}`)}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

function Navigation() {
  const { t } = useConsoleI18n()

  return (
    <nav aria-label={t('nav.label')}>
      <ul className="flex list-none flex-wrap gap-1 p-0 md:flex-col">
        {NAV.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              activeOptions={{ exact: item.to === '/' }}
              className={cx(
                'block min-h-touch rounded-card px-3 py-2 font-medium text-body text-muted',
                'hover:bg-surface',
              )}
              activeProps={{ className: 'bg-brand-50 text-brand-700' }}
            >
              {t(item.label)}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
